import { computed, Injectable, signal } from '@angular/core';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  Firestore,
  onSnapshot,
  updateDoc,
} from '@angular/fire/firestore';
import { forkJoin, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { IMenu, IPage } from '../../models';
import { PageService } from './page.service';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private collectionName = 'menuSections';

  private menus = signal<IMenu[]>([]);

  menuList = computed(() => this.menus());

  getMenuBySlug(slug?: string): IMenu | undefined {
    if (!slug) {
      return undefined;
    }
    const menus = this.menus();

    const searchInTree = (items: IMenu[]): IMenu | undefined => {
      for (const item of items) {
        if (item.slug === slug) {
          return item;
        }
        if (item.children && item.children.length > 0) {
          const found = searchInTree(item.children);
          if (found) {
            return found;
          }
        }
      }
      return undefined;
    };

    return searchInTree(menus);
  }

  constructor(private firestore: Firestore, private pageService: PageService) {}

  getAllMenu(): Observable<IMenu[]> {
    if (this.menus().length > 0) {
      return of(this.menus());
    }

    const menuSectionsRef = collection(this.firestore, this.collectionName);

    return new Observable<IMenu[]>(observer => {
      try {
        // Подписываемся на изменения (Real-time)
        const unsubscribe = onSnapshot(
          menuSectionsRef,
          snapshot => {
            // Преобразуем данные
            const allItems = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              children: [] as IMenu[],
            })) as IMenu[];

            // Создаем карту для быстрого доступа по id
            const itemsMap = new Map<string, IMenu>();
            allItems.forEach(item => {
              itemsMap.set(item.id ?? 'undefined', item);
            });

            // Строим иерархическую структуру
            const rootItems: IMenu[] = [];

            allItems.forEach(item => {
              if (item.parentId) {
                // Если есть parentId, добавляем к родителю
                const parent = itemsMap.get(item.parentId);
                if (parent) {
                  if (!parent.children) {
                    parent.children = [];
                  }
                  parent.children.push(item);
                }
              } else {
                // Если нет parentId, это корневой элемент
                rootItems.push(item);
              }
            });

            // Сортируем по order
            const sortByOrder = (items: IMenu[]): IMenu[] => {
              return items
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(item => ({
                  ...item,
                  children: item.children ? sortByOrder(item.children) : [],
                }));
            };

            const sortedRootItems = sortByOrder(rootItems);

            // Собираем все уникальные pageId из всех элементов меню (включая вложенные)
            const collectPageIds = (items: IMenu[]): Set<string> => {
              const pageIds = new Set<string>();
              items.forEach(item => {
                if (item.pageId) {
                  pageIds.add(item.pageId);
                }
                if (item.children && item.children.length > 0) {
                  collectPageIds(item.children).forEach(id => pageIds.add(id));
                }
              });
              return pageIds;
            };

            const pageIds = Array.from(collectPageIds(sortedRootItems));
            // Если есть pageId, загружаем страницы
            if (pageIds.length > 0) {
              const pageObservables = pageIds.map(pageId =>
                this.pageService.getById(pageId).pipe(
                  take(1), // Берем только первое значение, чтобы forkJoin мог завершиться
                  map(page => ({ pageId, page })),
                ),
              );

              forkJoin(pageObservables).subscribe({
                next: pageResults => {
                  // Создаем карту страниц по pageId
                  const pagesMap = new Map<string, IPage>();
                  pageResults.forEach(({ pageId, page }) => {
                    if (page) {
                      pagesMap.set(pageId, page);
                    }
                  });

                  // Функция для добавления страниц к элементам меню
                  const attachPages = (items: IMenu[]): IMenu[] => {
                    return items.map(item => {
                      const menuItem: IMenu = {
                        ...item,
                        page: item.pageId ? pagesMap.get(item.pageId) : undefined,
                        children: item.children ? attachPages(item.children) : [],
                      };
                      return menuItem;
                    });
                  };

                  const menuWithPages = attachPages(sortedRootItems);
                  this.menus.set(menuWithPages);
                  console.log('menuWithPages', menuWithPages);
                  observer.next(menuWithPages);
                },
                error: () => {
                  // В случае ошибки все равно возвращаем меню без страниц
                  this.menus.set(sortedRootItems);
                  console.log('sortedRootItems', sortedRootItems);
                  observer.next(sortedRootItems);
                },
              });
            } else {
              // Если нет pageId, просто отправляем меню без страниц
              this.menus.set(sortedRootItems);
              console.log('sortedRootItems', sortedRootItems);
              observer.next(sortedRootItems);
            }
          },
          error => {
            // Обрабатываем ошибки
            observer.error(error);
          },
        );

        // Функция очистки (вызывается, когда компонент уничтожается/отписывается)
        return () => unsubscribe();
      } catch (error) {
        observer.error(error);
        return () => {
          // No cleanup needed if error occurred before subscription
        };
      }
    });
  }

  getAll(parentId?: string): Observable<IMenu[]> {
    const menuSectionsRef = collection(this.firestore, this.collectionName);

    return new Observable<IMenu[]>(observer => {
      // Подписываемся на изменения (Real-time)
      const unsubscribe = onSnapshot(
        menuSectionsRef,
        snapshot => {
          // Преобразуем данные
          const data = snapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data(),
            }))
            .filter(item => {
              // Для root элементов: parentId должен быть null или отсутствовать
              const itemParentId = (item as IMenu).parentId;
              return parentId ? itemParentId === parentId : !itemParentId;
            }) as IMenu[];

          // Отправляем в поток
          observer.next(data);
        },
        error => {
          // Обрабатываем ошибки
          observer.error(error);
        },
      );

      // Функция очистки (вызывается, когда компонент уничтожается/отписывается)
      return () => unsubscribe();
    });
  }

  async create(data: Omit<IMenu, 'id'>): Promise<string> {
    const menuSectionsRef = collection(this.firestore, this.collectionName);
    const docRef = await addDoc(menuSectionsRef, data);
    return docRef.id;
  }

  async update(id: string, data: Partial<IMenu>): Promise<void> {
    const docRef = doc(this.firestore, this.collectionName, id);
    const updateData = { ...data };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (updateData as any).id;
    await updateDoc(docRef, updateData);
  }

  async delete(id: string): Promise<void> {
    const docRef = doc(this.firestore, this.collectionName, id);
    await deleteDoc(docRef);
  }
}
